/**
 * Getting signed in. There are two doors, and they open onto the same account.
 *
 * **Phone number + SMS code** is the front door (D-61). Firebase Auth verifies the
 * number and hands back a uid, and that uid IS the `users/{uid}` document id — so the
 * log you are shown is your own by construction, not because a lookup table said so.
 *
 * **The magic link** is the shortcut from the chat thread (D-11, B-11): one tap, no code
 * to type. It mints a custom token for the same Auth uid, so both doors are the same
 * session. It is kept alongside rather than replaced because the tap is worth having;
 * the cost is that C-05 stays open, which is a deliberate trade.
 *
 * Firebase persists the session in IndexedDB afterwards, so neither door is a per-visit
 * ritual.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  RecaptchaVerifier,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPhoneNumber,
  signOut,
  type ConfirmationResult,
  type User,
} from "firebase/auth";
import { EXCHANGE_URL, auth } from "./firebase.js";

interface AuthState {
  /** Undefined while Firebase is still restoring a persisted session. */
  user: User | null | undefined;
  signOut: () => Promise<void>;
}

const Context = createContext<AuthState>({ user: undefined, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  return (
    <Context.Provider value={{ user, signOut: () => signOut(auth) }}>{children}</Context.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(Context);
}

export type ExchangeError = "invalid" | "network";

export type PhoneError =
  | { kind: "badNumber" | "tooMany" | "badCode" | "expired" }
  /**
   * Anything not recognised above. It carries the SDK's own code, and the screen shows
   * it, because "couldn't send the code" is useless to the one person who could act on
   * it — every distinct cause here is a project-configuration problem (C-19, C-20) that
   * reads identically from the outside.
   */
  | { kind: "failed"; code: string };

/**
 * Send an SMS code, and return the confirmation handle needed to check it.
 *
 * The reCAPTCHA verifier is invisible and rebuilt for each attempt: a verifier is
 * single-use, and reusing a spent one fails in a way that reads as "the number is
 * wrong". `container` must be an element that is in the document.
 */
export async function sendPhoneCode(
  phoneNumber: string,
  container: HTMLElement,
): Promise<{ confirmation: ConfirmationResult } | { error: PhoneError }> {
  let verifier: RecaptchaVerifier | null = null;
  try {
    verifier = new RecaptchaVerifier(auth, container, { size: "invisible" });
    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    return { confirmation };
  } catch (error) {
    verifier?.clear();
    // Phone sign-in fails in several ways that all look identical to the user, and the
    // SDK's own message is the only thing that tells them apart. Console, not the
    // screen: it is for whoever is debugging, not for whoever is signing in.
    console.error("[altrtrack] phone sign-in failed", error);
    return { error: classify(error) };
  }
}

/** Check the code the SMS carried. Success signs in; `onAuthStateChanged` does the rest. */
export async function confirmPhoneCode(
  confirmation: ConfirmationResult,
  code: string,
): Promise<PhoneError | null> {
  try {
    await confirmation.confirm(code);
    return null;
  } catch (error) {
    return classify(error);
  }
}

function classify(error: unknown): PhoneError {
  const code =
    (typeof error === "object" && error !== null ? (error as { code?: string }).code : "") ?? "";
  switch (code) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return { kind: "badNumber" };
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return { kind: "tooMany" };
    case "auth/invalid-verification-code":
      return { kind: "badCode" };
    case "auth/code-expired":
      return { kind: "expired" };
    default:
      return { kind: "failed", code: code || String(error) };
  }
}

/**
 * Redeem a token and sign in.
 *
 * The endpoint answers every failure identically by design (D-59), so there is one
 * error to report and it is the same sentence in each case: the link is spent. That is
 * also true — expired, reused and mistyped are all "get a new one".
 */
export async function signInWithMagicToken(token: string): Promise<ExchangeError | null> {
  let response: Response;
  try {
    response = await fetch(EXCHANGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    return "network";
  }

  if (!response.ok) return "invalid";

  try {
    const { customToken } = (await response.json()) as { customToken?: string };
    if (!customToken) return "invalid";
    await signInWithCustomToken(auth, customToken);
    return null;
  } catch {
    return "invalid";
  }
}
